import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import axios from 'axios';
import {
  getMeetingDisplayName,
  formatMeetingDateTime,
  formatDuration
} from '../utils/meetingTemplates';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:3001';

const MeetingResponsePage = () => {
  const { meetingId, token } = useParams();

  const [meeting, setMeeting] = useState(null);
  const [attendee, setAttendee] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const [responses, setResponses] = useState([]);
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  // Hämta möte och deltagarinfo
  useEffect(() => {
    const fetchMeeting = async () => {
      try {
        setLoading(true);
        setError(null);

        const response = await axios.get(
          `${API_URL}/api/meetings/response/${meetingId}/${token}`
        );

        setMeeting(response.data.meeting);
        setAttendee(response.data.attendee);

        // Initiera responses om deltagaren redan har svarat
        if (response.data.attendee.proposalResponses?.length > 0) {
          setResponses(response.data.attendee.proposalResponses);
          setSubmitted(true);
        } else {
          // Initiera med neutrala värden
          setResponses(
            (response.data.meeting.proposals || []).map((_, idx) => ({
              proposalIndex: idx,
              canAttend: null
            }))
          );
        }
      } catch (err) {
        console.error('Fel vid hämtning av möte:', err);
        setError(err.response?.data?.message || 'Kunde inte ladda mötet');
      } finally {
        setLoading(false);
      }
    };

    if (meetingId && token) {
      fetchMeeting();
    }
  }, [meetingId, token]);

  // Hantera svar på förslag
  const handleResponseChange = (proposalIndex, canAttend) => {
    setResponses(prev =>
      prev.map(r =>
        r.proposalIndex === proposalIndex
          ? { ...r, canAttend }
          : r
      )
    );
  };

  // Skicka svar
  const handleSubmit = async () => {
    // Validera att alla förslag har svar
    const unanswered = responses.filter(r => r.canAttend === null);
    if (unanswered.length > 0) {
      alert('Vänligen markera om du kan eller inte för alla föreslagna tider.');
      return;
    }

    try {
      setSubmitting(true);
      await axios.post(`${API_URL}/api/meetings/${meetingId}/respond`, {
        token,
        responses
      });
      setSubmitted(true);
    } catch (err) {
      console.error('Fel vid skickande av svar:', err);
      alert(err.response?.data?.message || 'Kunde inte skicka svar');
    } finally {
      setSubmitting(false);
    }
  };

  // Loading state
  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-pink-500"></div>
      </div>
    );
  }

  // Error state
  if (error) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center p-4">
        <div className="bg-white rounded-lg shadow-lg p-8 max-w-md w-full text-center">
          <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <svg className="w-8 h-8 text-red-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
            </svg>
          </div>
          <h2 className="text-xl font-semibold text-gray-900 mb-2">Något gick fel</h2>
          <p className="text-gray-600">{error}</p>
          <p className="text-sm text-gray-500 mt-4">
            Länken kan ha gått ut eller vara ogiltig.
          </p>
        </div>
      </div>
    );
  }

  // Meeting already booked/completed
  if (meeting?.status === 'scheduled' || meeting?.status === 'completed') {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center p-4">
        <div className="bg-white rounded-lg shadow-lg p-8 max-w-md w-full text-center">
          <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <svg className="w-8 h-8 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7" />
            </svg>
          </div>
          <h2 className="text-xl font-semibold text-gray-900 mb-2">
            Mötet är {meeting.status === 'scheduled' ? 'bokat' : 'genomfört'}
          </h2>
          <p className="text-gray-600">
            {meeting.displayName || getMeetingDisplayName(meeting)}
          </p>
          {meeting.status === 'scheduled' && (
            <p className="text-sm text-gray-500 mt-2">
              Du bör ha fått en kalenderinbjudan med mötesdetaljerna.
            </p>
          )}
        </div>
      </div>
    );
  }

  // Submitted confirmation
  if (submitted) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center p-4">
        <div className="bg-white rounded-lg shadow-lg p-8 max-w-md w-full text-center">
          <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <svg className="w-8 h-8 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7" />
            </svg>
          </div>
          <h2 className="text-xl font-semibold text-gray-900 mb-2">Tack för ditt svar!</h2>
          <p className="text-gray-600">
            Ditt svar har registrerats. Du får en kalenderinbjudan när mötet är bokat.
          </p>

          {/* Visa vad deltagaren svarade */}
          <div className="mt-6 text-left bg-gray-50 rounded-lg p-4">
            <h3 className="text-sm font-medium text-gray-700 mb-2">Dina svar:</h3>
            <div className="space-y-2">
              {meeting?.proposals?.map((proposal, idx) => {
                const response = responses.find(r => r.proposalIndex === idx);
                return (
                  <div key={idx} className="flex items-center justify-between text-sm">
                    <span className="text-gray-600">
                      {proposal.displayText || formatMeetingDateTime(proposal.date, proposal.time)}
                    </span>
                    {response?.canAttend ? (
                      <span className="text-green-600 flex items-center">
                        <svg className="w-4 h-4 mr-1" fill="currentColor" viewBox="0 0 20 20">
                          <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                        </svg>
                        Kan
                      </span>
                    ) : (
                      <span className="text-red-600 flex items-center">
                        <svg className="w-4 h-4 mr-1" fill="currentColor" viewBox="0 0 20 20">
                          <path fillRule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clipRule="evenodd" />
                        </svg>
                        Kan inte
                      </span>
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Main response form
  return (
    <div className="min-h-screen bg-gray-50 py-8 px-4">
      <div className="max-w-2xl mx-auto">
        {/* Header */}
        <div className="bg-white rounded-lg shadow-lg overflow-hidden mb-6">
          {/* Pink header bar */}
          <div className="bg-gradient-to-r from-pink-500 to-pink-400 px-6 py-4">
            <h1 className="text-xl font-semibold text-white">
              {meeting?.displayName || getMeetingDisplayName(meeting)}
            </h1>
            {meeting?.projectId && (
              <p className="text-pink-100 text-sm mt-1">#{meeting.projectId}</p>
            )}
          </div>

          {/* Meeting info */}
          <div className="p-6 space-y-4">
            <div className="flex items-center text-gray-600">
              <svg className="w-5 h-5 mr-3 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
              <span>Varaktighet: {formatDuration(meeting?.duration)}</span>
            </div>

            <div className="flex items-center text-gray-600">
              <svg className="w-5 h-5 mr-3 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
              </svg>
              <div>
                <span className="font-medium">{meeting?.location?.displayName || 'Plats ej angiven'}</span>
                {meeting?.location?.address && (
                  <span className="text-gray-500 ml-1">({meeting.location.address})</span>
                )}
              </div>
            </div>

            {/* Agenda */}
            {meeting?.agenda?.length > 0 && (
              <div className="border-t pt-4 mt-4">
                <h3 className="text-sm font-medium text-gray-700 mb-2">Agenda</h3>
                <ul className="list-disc list-inside text-gray-600 text-sm space-y-1">
                  {meeting.agenda.map((item, idx) => (
                    <li key={idx}>{item.title}</li>
                  ))}
                </ul>
              </div>
            )}
          </div>
        </div>

        {/* Response form */}
        <div className="bg-white rounded-lg shadow-lg p-6">
          <h2 className="text-lg font-semibold text-gray-900 mb-2">
            Hej {attendee?.name?.split(' ')[0]}!
          </h2>
          <p className="text-gray-600 mb-6">
            Markera vilka tider som passar dig. När alla deltagare svarat bokas mötet automatiskt.
          </p>

          {/* Proposals */}
          <div className="space-y-4 mb-6">
            {meeting?.proposals?.map((proposal, idx) => {
              const response = responses.find(r => r.proposalIndex === idx);
              const canAttend = response?.canAttend;

              return (
                <div
                  key={idx}
                  className={`
                    border rounded-lg p-4 transition-all
                    ${canAttend === true ? 'border-green-300 bg-green-50' : ''}
                    ${canAttend === false ? 'border-red-200 bg-red-50' : ''}
                    ${canAttend === null ? 'border-gray-200' : ''}
                  `}
                >
                  <div className="flex items-center justify-between">
                    <div>
                      <span className="font-medium text-gray-900">
                        {proposal.displayText || formatMeetingDateTime(proposal.date, proposal.time)}
                      </span>
                    </div>

                    <div className="flex gap-2">
                      <button
                        onClick={() => handleResponseChange(idx, true)}
                        className={`
                          flex items-center px-4 py-2 rounded-lg text-sm font-medium transition-all
                          ${canAttend === true
                            ? 'bg-green-500 text-white'
                            : 'bg-gray-100 text-gray-700 hover:bg-green-100 hover:text-green-700'
                          }
                        `}
                      >
                        <svg className="w-4 h-4 mr-1" fill="currentColor" viewBox="0 0 20 20">
                          <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                        </svg>
                        Kan
                      </button>
                      <button
                        onClick={() => handleResponseChange(idx, false)}
                        className={`
                          flex items-center px-4 py-2 rounded-lg text-sm font-medium transition-all
                          ${canAttend === false
                            ? 'bg-red-500 text-white'
                            : 'bg-gray-100 text-gray-700 hover:bg-red-100 hover:text-red-700'
                          }
                        `}
                      >
                        <svg className="w-4 h-4 mr-1" fill="currentColor" viewBox="0 0 20 20">
                          <path fillRule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clipRule="evenodd" />
                        </svg>
                        Kan inte
                      </button>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>

          {/* Submit button */}
          <button
            onClick={handleSubmit}
            disabled={submitting || responses.some(r => r.canAttend === null)}
            className={`
              w-full py-3 rounded-lg font-medium text-white transition-all
              ${submitting || responses.some(r => r.canAttend === null)
                ? 'bg-gray-400 cursor-not-allowed'
                : 'bg-pink-500 hover:bg-pink-600'
              }
            `}
          >
            {submitting ? 'Skickar...' : 'Skicka svar'}
          </button>

          {responses.some(r => r.canAttend === null) && (
            <p className="text-sm text-gray-500 text-center mt-2">
              Markera alla tider för att kunna skicka
            </p>
          )}
        </div>

        {/* Leadme branding */}
        <div className="text-center mt-6">
          <p className="text-sm text-gray-400">
            Powered by Leadme
          </p>
        </div>
      </div>
    </div>
  );
};

export default MeetingResponsePage;
